<template>
  <div class="collection-list" :class="{ 'is-mobile': isMobile }">
    <!-- 顶部导航栏 -->
    <header class="top-header">
      <div class="header-left">
        <div class="logo-section">
          <i class="el-icon-reading"></i>
          <span class="logo-text">万维智慧云文档</span>
        </div>
      </div>
      
      <div class="header-right">
        <el-dropdown @command="handleUserCommand" class="user-menu">
          <div class="user-info-box">
            <el-avatar :size="32" icon="el-icon-user-solid" :style="{background: roleColor}"></el-avatar>
            <span class="username-text">{{ userInfo.nickname || userInfo.username }}</span>
          </div>
          <el-dropdown-menu slot="dropdown">
            <el-dropdown-item disabled>
              <div class="user-card">
                <div class="user-card-name">{{ userInfo.username }}</div>
                <el-tag :type="roleTagType" size="mini">{{ roleText }}</el-tag>
              </div>
            </el-dropdown-item>
            <el-dropdown-item v-if="isAdmin" command="admin" divided>
              <i class="el-icon-s-tools"></i> 进入后台
            </el-dropdown-item>
            <el-dropdown-item :divided="!isAdmin" command="logout">
              <i class="el-icon-switch-button"></i> 退出登录
            </el-dropdown-item>
          </el-dropdown-menu>
        </el-dropdown>
      </div>
    </header>
    
    <!-- 页面头部 -->
    <div class="page-header">
      <div class="header-left">
        <h1 class="page-title">
          <i class="el-icon-folder-opened"></i>
          文集列表
        </h1>
        <p class="page-subtitle">浏览和管理您的文档文集</p>
      </div>
      
      <div class="header-right">
        <!-- 搜索框 -->
        <div class="search-section">
          <el-input
            v-model="searchKeyword"
            placeholder="搜索文档..."
            prefix-icon="el-icon-search"
            class="search-box"
            clearable
            @keyup.enter.native="handleSearchEnter"
            @input="handleSearchInput"
          />
          <el-button 
            type="primary" 
            icon="el-icon-search" 
            class="search-btn"
            @click="handleSearchEnter"
          >
            搜索
          </el-button>
        </div>
        
        <!-- 操作按钮区域 -->
        <div class="action-buttons">
          <!-- 筛选按钮（仅管理员可见） -->
          <el-dropdown v-if="isAdmin" @command="handleFilter" trigger="click" class="filter-dropdown">
            <el-button size="small">
              筛选<i class="el-icon-arrow-down el-icon--right"></i>
            </el-button>
            <el-dropdown-menu slot="dropdown">
              <el-dropdown-item command="all">全部文集</el-dropdown-item>
              <el-dropdown-item command="public">公开文集</el-dropdown-item>
              <el-dropdown-item command="private">私有文集</el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>
          
          <!-- 排序按钮（仅管理员可见） -->
          <!-- <el-dropdown v-if="isAdmin" @command="handleSort" trigger="click" class="sort-dropdown">
            <el-button size="small">
              排序<i class="el-icon-arrow-down el-icon--right"></i>
            </el-button>
            <el-dropdown-menu slot="dropdown">
              <el-dropdown-item command="createTime">创建时间</el-dropdown-item>
              <el-dropdown-item command="updateTime">更新时间</el-dropdown-item>
              <el-dropdown-item command="order">自定义排序</el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown> -->
          
          <el-button v-if="canEdit" type="primary" size="small" icon="el-icon-plus" @click="handleCreate">
            新建文集
          </el-button>
        </div>
      </div>
    </div>
    
    <!-- 搜索结果 -->
    <SearchResults 
      v-if="isSearching"
      :searchKeyword="currentSearchKeyword"
      :searchResults="searchResults"
      :loading="searchLoading"
      @result-click="handleSearchResultClick"
    />
    
    <!-- 文集卡片网格 -->
    <div v-else class="collection-grid" v-loading="loading">
      <div 
        v-for="collection in collections" 
        :key="collection.collectionId"
        class="collection-card"
        @click="handleCollectionClick(collection)"
      >
        <div class="card-header">
          <div class="collection-title">
            <div class="title-icon" :class="getCollectionIconClass(collection.permission)">
              <i :class="collection.icon || 'el-icon-folder'"></i>
            </div>
            <h3 class="title-text">{{ collection.collectionName }}</h3>
          </div>
          <div class="collection-badge">
            <el-tag 
              :type="collection.permission === 'public' ? 'success' : 'warning'" 
              size="mini"
            >
              {{ collection.permission === 'public' ? '公开' : '私有' }}
            </el-tag>
          </div>
        </div>
        
        <div class="card-meta">
          <div class="meta-item">
            <i class="el-icon-user"></i>
            <span>作者: {{ collection.creatorName || '未知' }}</span>
          </div>
          <div class="meta-item">
            <i class="el-icon-time"></i>
            <span>最新: {{ formatDate(collection.updateTime) }}</span>
          </div>
        </div>
        
        <div class="card-description">
          <p class="description-text">
            {{ collection.description || '此文集没有填写简介' }}
          </p>
        </div>
        
        <div class="card-footer">
          <div class="footer-left">
            <span class="doc-count">{{ getDocumentCount(collection) }} 个文档</span>
          </div>
          <div class="footer-right" v-if="canEdit">
            <el-button-group size="mini">
              <el-tooltip content="导出Word" placement="top" v-if="isAdmin">
                <el-button 
                  icon="el-icon-download" 
                  type="success"
                  @click.stop="handleExport(collection)"
                ></el-button>
              </el-tooltip>
              <el-tooltip content="复制文集" placement="top">
                <el-button 
                  icon="el-icon-document-copy" 
                  type="warning"
                  @click.stop="handleCopy(collection)"
                ></el-button>
              </el-tooltip>
              <el-tooltip content="编辑文集" placement="top">
                <el-button 
                  icon="el-icon-edit" 
                  @click.stop="handleEdit(collection)"
                ></el-button>
              </el-tooltip>
              <el-tooltip content="删除文集" placement="top">
                <el-button 
                  icon="el-icon-delete" 
                  type="danger"
                  @click.stop="handleDelete(collection)"
                ></el-button>
              </el-tooltip>
            </el-button-group>
          </div>
        </div>
      </div>
    </div>
    
    <!-- 空状态 -->
    <div v-if="!loading && collections.length === 0" class="empty-state">
      <el-empty description="暂无文集">
        <el-button v-if="canEdit" type="primary" @click="handleCreate">
          <i class="el-icon-plus"></i> 创建第一个文集
        </el-button>
      </el-empty>
    </div>
    
    <!-- 分页 -->
    <div v-if="collections.length > 0" class="pagination-wrapper">
      <el-pagination
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
        :current-page="currentPage"
        :page-sizes="[12, 24, 48]"
        :page-size="pageSize"
        layout="total, sizes, prev, pager, next, jumper"
        :total="total"
      />
    </div>
    
    <!-- 创建/编辑文集对话框 -->
    <el-dialog
      :title="dialogTitle"
      :visible.sync="dialogVisible"
      :width="isMobile ? '90%' : '600px'"
      :close-on-click-modal="false"
      class="collection-dialog"
    >
      <el-form :model="collectionForm" :rules="collectionRules" ref="collectionForm" :label-width="isMobile ? '80px' : '100px'">
        <el-form-item label="文集名称" prop="collectionName">
          <el-input 
            v-model="collectionForm.collectionName" 
            placeholder="请输入文集名称"
            maxlength="50"
            show-word-limit
          ></el-input>
        </el-form-item>
        
        <el-form-item label="文集描述" prop="description">
          <el-input 
            v-model="collectionForm.description" 
            type="textarea"
            :rows="4"
            placeholder="请输入文集描述"
            maxlength="500"
            show-word-limit
          ></el-input>
        </el-form-item>
        
        <el-form-item label="访问权限" prop="permission">
          <el-radio-group v-model="collectionForm.permission">
            <el-radio label="public">公开 - 所有用户都可以访问</el-radio>
            <el-radio label="private" v-if="isAdmin">私有 - 仅管理员可以访问</el-radio>
          </el-radio-group>
        </el-form-item>
        
        <el-form-item label="图标" prop="icon" v-if="isAdmin">
          <div class="icon-selector" :class="{ 'mobile': isMobile }">
            <div class="icon-preview">
              <i :class="collectionForm.icon || 'el-icon-folder'"></i>
            </div>
            <div class="icon-grid" :class="{ 'mobile': isMobile }">
              <div
                v-for="icon in iconOptions"
                :key="icon.value"
                class="icon-item"
                :class="{ 'active': collectionForm.icon === icon.value }"
                @click="collectionForm.icon = icon.value"
              >
                <i :class="icon.value"></i>
              </div>
            </div>
          </div>
        </el-form-item>
        
        <el-form-item label="排序" prop="order">
          <el-input-number v-model="collectionForm.order" :min="0" :max="999" controls-position="right"></el-input-number>
          <span class="form-tip">数字越小越靠前</span>
        </el-form-item>
      </el-form>
      
      <div slot="footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSave" :loading="saving">
          {{ saving ? '保存中...' : '确定' }}
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { COLLECTION_PATHS, DOCUMENT_PATHS } from '@/api/paths'
import { userUtils, authUtils } from '@/utils/auth'
import SearchResults from '@/pages/SearchResults.vue'

export default {
  name: 'CollectionList',
  components: {
    SearchResults
  },
  data() {
    return {
      userInfo: {},
      collections: [],
      loading: false,
      searchKeyword: '',
      searchResults: [],
      searchLoading: false,
      isSearching: false,
      currentSearchKeyword: '',
      screenWidth: window.innerWidth,
      currentFilter: '',
      currentSort: 'order',
      currentPage: 1,
      pageSize: 12,
      total: 0,
      
      dialogVisible: false,
      dialogTitle: '新建文集',
      saving: false,
      collectionForm: {
        collectionId: null,
        collectionName: '',
        description: '',
        permission: 'public',
        icon: 'el-icon-folder',
        order: 0
      },
      iconOptions: [
        { label: '自行车', value: 'el-icon-bicycle' },
        { label: '办公楼', value: 'el-icon-office-building' },
        { label: '学校', value: 'el-icon-school' },
        { label: '指南', value: 'el-icon-guide' },
        { label: '添加位置', value: 'el-icon-add-location' },
        { label: '地图位置', value: 'el-icon-map-location' },
        { label: '地点', value: 'el-icon-place' },
        { label: '硬币', value: 'el-icon-coin' },
        { label: '商品', value: 'el-icon-goods' },
        { label: '销售', value: 'el-icon-sell' },
        { label: '售罄', value: 'el-icon-sold-out' },
        { label: '钥匙', value: 'el-icon-key' },
        { label: '购物车（满）', value: 'el-icon-shopping-cart-full' },
        { label: '电话', value: 'el-icon-phone' },
        { label: '奖杯', value: 'el-icon-trophy' },
        { label: '打印机', value: 'el-icon-printer' },
        { label: '盒子', value: 'el-icon-box' },
        { label: '金钱', value: 'el-icon-money' },
        { label: '冰箱', value: 'el-icon-refrigerator' },
        { label: 'CPU', value: 'el-icon-cpu' },
        { label: '秒表', value: 'el-icon-stopwatch' },
        { label: '计时器', value: 'el-icon-timer' },
        { label: '收藏标签', value: 'el-icon-collection-tag' },
        { label: '外卖盒', value: 'el-icon-takeaway-box' },
        { label: '价格标签', value: 'el-icon-price-tag' },
        { label: '钱包', value: 'el-icon-wallet' },
        { label: '麦克风', value: 'el-icon-microphone' },
        { label: '麦克', value: 'el-icon-mic' },
        { label: '视频暂停', value: 'el-icon-video-pause' },
        { label: '视频相机', value: 'el-icon-video-camera' },
        { label: '视频播放', value: 'el-icon-video-play' },
        { label: '耳机', value: 'el-icon-headset' },
        { label: '显示器', value: 'el-icon-monitor' },
        { label: '电影', value: 'el-icon-film' },
        { label: '相机', value: 'el-icon-camera' },
        { label: '图片', value: 'el-icon-picture' },
        { label: '文档添加', value: 'el-icon-document-add' },
        { label: '文档', value: 'el-icon-document' },
        { label: '票据', value: 'el-icon-tickets' },
        { label: '收藏', value: 'el-icon-collection' },
        { label: '明信片', value: 'el-icon-postcard' },
        { label: '银行卡', value: 'el-icon-bank-card' },
        { label: '设置', value: 'el-icon-set-up' },
        { label: '文档删除', value: 'el-icon-document-delete' },
        { label: '文档检查', value: 'el-icon-document-checked' },
        { label: '数据板', value: 'el-icon-data-board' },
        { label: '数据分析', value: 'el-icon-data-analysis' },
        { label: '复制文档', value: 'el-icon-copy-document' },
        { label: '文件夹检查', value: 'el-icon-folder-checked' },
        { label: '文件', value: 'el-icon-files' },
        { label: '文件夹', value: 'el-icon-folder' },
        { label: '文件夹删除', value: 'el-icon-folder-delete' },
        { label: '文件夹移除', value: 'el-icon-folder-remove' },
        { label: '打开的文件夹', value: 'el-icon-folder-opened' },
        { label: '文档复制', value: 'el-icon-document-copy' },
        { label: '文档移除', value: 'el-icon-document-remove' },
        { label: '文件夹添加', value: 'el-icon-folder-add' },
        { label: '急救箱', value: 'el-icon-first-aid-kit' },
        { label: '阅读', value: 'el-icon-reading' },
        { label: '数据线', value: 'el-icon-data-line' },
        { label: '移动电话', value: 'el-icon-mobile-phone' },
        { label: '用户', value: 'el-icon-user' }
      ],
      collectionRules: {
        collectionName: [
          { required: true, message: '请输入文集名称', trigger: 'blur' },
          { min: 1, max: 50, message: '长度在 1 到 50 个字符', trigger: 'blur' }
        ]
      }
    }
  },
  computed: {
    isMobile() {
      // 优先使用设备检测，避免横屏时被误判为桌面端
      if (typeof navigator !== 'undefined') {
        const ua = navigator.userAgent || navigator.vendor || ''
        
        // 1. 检测明确的移动设备标识
        const mobileRegex = /(android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini)/i
        if (mobileRegex.test(ua)) {
          return true
        }
        
        // 2. 检测移动设备的User Agent特征
        const mobilePlatforms = /Mobile|mobile|Mobi|mobi/i
        if (mobilePlatforms.test(ua)) {
          return true
        }
        
        // 3. 检测触摸设备并结合屏幕尺寸
        if ('ontouchstart' in window || navigator.maxTouchPoints > 0) {
          // 使用屏幕的最小尺寸来判断，避免横屏影响
          const screenMinSize = window.screen ? Math.min(window.screen.width, window.screen.height) : 0
          const screenMaxSize = window.screen ? Math.max(window.screen.width, window.screen.height) : 0
          
          // 如果最小尺寸小于等于768，或者最大尺寸小于等于1024，很可能是移动设备
          if (screenMinSize <= 768 || (screenMaxSize <= 1024 && screenMinSize <= 768)) {
            return true
          }
        }
      }
      
      // 4. 兜底：如果以上都不满足，但当前窗口宽度小于等于768，也认为是移动端
      // 这主要是为了处理一些特殊情况和桌面端的小窗口
      return this.screenWidth <= 768
    },
    canEdit() {
      return authUtils.isEditor()
    },
    isAdmin() {
      return authUtils.isAdmin()
    },
    roleText() {
      const roleMap = {
        'admin': '管理员',
        'editor': '编辑员',
        'viewer': '普通用户'
      }
      return roleMap[this.userInfo.role] || '未知'
    },
    roleTagType() {
      const typeMap = {
        'admin': 'danger',
        'editor': 'warning',
        'viewer': 'info'
      }
      return typeMap[this.userInfo.role] || 'info'
    },
    roleColor() {
      const colorMap = {
        'admin': '#f56c6c',
        'editor': '#e6a23c',
        'viewer': '#909399'
      }
      return colorMap[this.userInfo.role] || '#409EFF'
    }
  },
  mounted() {
    this.getUserInfo()
    this.loadCollections()
    // 屏幕宽度监听用于区分移动端与桌面端
    this.__onResize = () => { this.screenWidth = window.innerWidth }
    window.addEventListener('resize', this.__onResize)
  },
  beforeDestroy() {
    if (this.__onResize) {
      window.removeEventListener('resize', this.__onResize)
    }
  },
  methods: {
    getUserInfo() {
      this.userInfo = userUtils.getUserInfo() || {}
    },
    
    async loadCollections() {
      this.loading = true
      try {
        const params = {
          page: this.currentPage,
          size: this.pageSize
        }
        
        // 移除了搜索文集功能，现在搜索框用于搜索文档
        
        // 添加权限筛选
        if (this.currentFilter) {
          params.permission = this.currentFilter
        }
        
        const response = await this.$get(COLLECTION_PATHS.GET_COLLECTION_PAGE, { params })
        if (response.data && response.data.code === 200) {
          const data = response.data.data
          this.collections = data.list || []
          this.total = data.total || 0
          
          // 如果是viewer且只有一个文集权限，自动进入该文集
          this.autoRedirectForSingleCollection(data)
        }
      } catch (error) {
        this.$message.error('加载文集列表失败')
        console.error(error)
      } finally {
        this.loading = false
      }
    },
    
    // 自动跳转到单个文集（仅对viewer且只有1个文集时）
    autoRedirectForSingleCollection(data) {
      // 只在初次加载（第一页）、无筛选时自动跳转
      if (this.currentPage === 1 && !this.currentFilter) {
        // 检查是否为viewer且总数为1
        if (this.userInfo.role === 'viewer' && data.total === 1 && this.collections.length === 1) {
          const collection = this.collections[0]
          // 自动跳转到该文集
          this.$nextTick(() => {
            if (this.$route.path.startsWith('/admin')) {
              this.$router.replace(`/admin/collections/${collection.collectionId}`)
            } else {
              this.$router.replace(`/collections/${collection.collectionId}`)
            }
          })
        }
      }
    },
    
    // 搜索输入处理
    handleSearchInput() {
      // 如果搜索词为空且正在搜索，退出搜索状态
      if (!this.searchKeyword.trim() && this.isSearching) {
        this.isSearching = false
        this.searchResults = []
        this.currentSearchKeyword = ''
      }
    },
    
    // 按回车搜索
    handleSearchEnter() {
      if (!this.searchKeyword.trim()) {
        this.$message.warning('请输入搜索关键词')
        return
      }
      this.performSearch()
      
      // 让搜索按钮和输入框失焦
      this.$nextTick(() => {
        // 让搜索输入框失焦
        const searchInput = this.$el?.querySelector('.search-box .el-input__inner')
        if (searchInput) {
          searchInput.blur()
        }
        // 让搜索按钮失焦
        const searchButton = this.$el?.querySelector('.search-btn')
        if (searchButton) {
          searchButton.blur()
        }
        // 如果当前焦点在任何元素上，也移除
        if (document.activeElement) {
          document.activeElement.blur()
        }
      })
    },
    
    // 执行搜索
    async performSearch() {
      if (!this.searchKeyword.trim()) {
        return
      }
      
      this.isSearching = true
      this.currentSearchKeyword = this.searchKeyword.trim()
      this.searchLoading = true
      this.searchResults = []
      
      try {
        const response = await this.$get(DOCUMENT_PATHS.SEARCH_DOCUMENTS, {
          params: { keyword: this.currentSearchKeyword }
        })
        
        if (response.data && response.data.code === 200) {
          this.searchResults = response.data.data || []
        } else {
          this.searchResults = []
          this.$message.error(response.data.msg || '搜索失败')
        }
      } catch (error) {
        console.error('搜索失败:', error)
        this.$message.error('搜索失败')
        this.searchResults = []
      } finally {
        this.searchLoading = false
      }
    },
    
    // 搜索结果点击处理
    handleSearchResultClick(doc) {
      // 退出搜索状态
      this.isSearching = false
      this.searchKeyword = ''
      this.searchResults = []
      this.currentSearchKeyword = ''
      
      // 跳转到文档
      this.$router.push(`/collections/${doc.collectionId}/documents/${doc.documentId}`)
    },
    
    handleFilter(command) {
      // 设置筛选条件
      if (command === 'all') {
        this.currentFilter = ''
      } else {
        this.currentFilter = command
      }
      // 重置到第一页并加载
      this.currentPage = 1
      this.loadCollections()
    },
    
    handleSort(command) {
      this.currentSort = command
      // 排序功能暂时由数据库默认排序处理（order ASC, create_time DESC）
      // 如果需要前端切换排序，可以添加sortBy参数到API
    },
    
    handleCollectionClick(collection) {
      // 根据当前路由判断跳转路径
      if (this.$route.path.startsWith('/admin')) {
        this.$router.push(`/admin/collections/${collection.collectionId}`)
      } else {
        this.$router.push(`/collections/${collection.collectionId}`)
      }
    },
    
    handleCreate() {
      this.dialogTitle = '新建文集'
      this.collectionForm = {
        collectionId: null,
        collectionName: '',
        description: '',
        permission: 'public',
        icon: 'el-icon-folder',
        order: 0
      }
      this.dialogVisible = true
      this.$nextTick(() => {
        this.$refs.collectionForm && this.$refs.collectionForm.clearValidate()
      })
    },
    
    handleEdit(collection) {
      this.dialogTitle = '编辑文集'
      this.collectionForm = {
        collectionId: collection.collectionId,
        collectionName: collection.collectionName,
        description: collection.description || '',
        permission: collection.permission,
        icon: collection.icon || 'el-icon-folder',
        order: collection.order || 0
      }
      this.dialogVisible = true
    },
    
    async handleSave() {
      try {
        await this.$refs.collectionForm.validate()
      } catch {
        return
      }
      
      this.saving = true
      try {
        const params = {
          collectionName: this.collectionForm.collectionName,
          description: this.collectionForm.description,
          permission: this.collectionForm.permission,
          icon: this.collectionForm.icon,
          order: this.collectionForm.order
        }
        
        let response
        if (this.collectionForm.collectionId) {
          params.collectionId = this.collectionForm.collectionId
          response = await this.$post(COLLECTION_PATHS.UPDATE_COLLECTION, params)
        } else {
          response = await this.$post(COLLECTION_PATHS.CREATE_COLLECTION, params)
        }
        
        if (response.data && response.data.code === 200) {
          this.$message.success(this.collectionForm.collectionId ? '更新成功' : '创建成功')
          this.dialogVisible = false
          this.loadCollections()
        }
      } catch (error) {
        this.$message.error(this.collectionForm.collectionId ? '更新失败' : '创建失败')
        console.error(error)
      } finally {
        this.saving = false
      }
    },
    
    async handleCopy(collection) {
      try {
        await this.$confirm(`确定要复制文集 "${collection.collectionName}" 及其下所有文档吗？`, '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'info'
        })
        
        this.loading = true
        const response = await this.$post(COLLECTION_PATHS.COPY_COLLECTION, {
          collectionId: collection.collectionId
        })
        
        if (response.data && response.data.code === 200) {
          this.$message.success('复制文集成功')
          this.loadCollections()
        } else {
          this.$message.error(response.data.msg || '复制文集失败')
        }
      } catch (error) {
        if (error !== 'cancel') {
          this.$message.error('复制文集失败')
          console.error(error)
        }
      } finally {
        this.loading = false
      }
    },
    
    async handleDelete(collection) {
      try {
        await this.$confirm('确定要删除这个文集吗？删除后不可恢复。', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
        
        const response = await this.$post(COLLECTION_PATHS.DELETE_COLLECTION, {
          collectionId: collection.collectionId
        })
        
        if (response.data && response.data.code === 200) {
          this.$message.success('删除成功')
          this.loadCollections()
        }
      } catch (error) {
        if (error !== 'cancel') {
          this.$message.error('删除失败')
          console.error(error)
        }
      }
    },
    
    handleSizeChange(val) {
      this.pageSize = val
      this.currentPage = 1
      this.loadCollections()
    },
    
    handleCurrentChange(val) {
      this.currentPage = val
      this.loadCollections()
    },
    
    getCollectionIconClass(permission) {
      return permission === 'public' ? 'public-icon' : 'private-icon'
    },
    
    getDocumentCount(collection) {
      return collection.documentCount || 0
    },
    
    formatDate(dateStr) {
      if (!dateStr) return '-'
      const date = new Date(dateStr)
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    
    handleUserCommand(command) {
      if (command === 'logout') {
        this.logout()
      } else if (command === 'admin') {
        this.$router.push('/admin/users')
      }
    },
    
    logout() {
      this.$confirm('确定要退出登录吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        authUtils.clearAuth()
        this.$message.success('已退出登录')
        this.$router.push('/login')
      }).catch(() => {})
    },
    
    // 导出文集为Word
    async handleExport(collection) {
      try {
        const loading = this.$loading({
          lock: true,
          text: '正在导出文集，请稍候...',
          spinner: 'el-icon-loading',
          background: 'rgba(0, 0, 0, 0.7)'
        })
        
        // 导入API基础URL和认证工具
        const API_BASE_URL = require('@/utils/request').API_BASE_URL
        const { tokenUtils } = require('@/utils/auth')
        
        // 构建请求URL
        const token = tokenUtils.getToken()
        if (!token) {
          loading.close()
          this.$message.error('未登录或登录已过期')
          return
        }
        
        const fileName = encodeURIComponent(collection.collectionName + '.docx')
        const url = `${API_BASE_URL}/export/collection/${collection.collectionId}/word?fileName=${fileName}`
        
        // 使用XMLHttpRequest检查响应类型
        const xhr = new XMLHttpRequest()
        xhr.open('GET', url, true)
        xhr.setRequestHeader('Authorization', `Bearer ${token}`)
        
        xhr.onload = function() {
          loading.close()
          
          if (xhr.status === 200) {
            const contentType = xhr.getResponseHeader('Content-Type')
            
            // 检查是否返回JSON（缓存URL）
            if (contentType && contentType.includes('application/json')) {
              try {
                // 将arraybuffer转换为字符串
                const text = new TextDecoder('utf-8').decode(xhr.response)
                const result = JSON.parse(text)
                
                if (result.cached && result.url) {
                  // 使用缓存的OSS URL直接下载
                  const a = document.createElement('a')
                  a.href = result.url
                  a.download = collection.collectionName + '.docx'
                  document.body.appendChild(a)
                  a.click()
                  document.body.removeChild(a)
                  this.$message.success('导出成功（使用缓存）')
                } else {
                  this.$message.error('导出失败')
                }
              } catch (e) {
                console.error('解析响应失败:', e)
                this.$message.error('解析响应失败')
              }
            } else {
              // 直接返回的文件流
              const blob = new Blob([xhr.response], { 
                type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' 
              })
              const blobUrl = window.URL.createObjectURL(blob)
              const a = document.createElement('a')
              a.href = blobUrl
              a.download = collection.collectionName + '.docx'
              document.body.appendChild(a)
              a.click()
              window.URL.revokeObjectURL(blobUrl)
              document.body.removeChild(a)
              
              this.$message.success('导出成功')
            }
          } else {
            this.$message.error('导出失败')
          }
        }.bind(this)
        
        xhr.onerror = function() {
          loading.close()
          this.$message.error('导出失败')
        }.bind(this)
        
        // 设置响应类型为arraybuffer以支持两种响应
        xhr.responseType = 'arraybuffer'
        xhr.send()
        
      } catch (error) {
        console.error('导出失败:', error)
        this.$message.error('导出失败：' + error.message)
      }
    }
  }
}
</script>

<style scoped>
.collection-list {
  background: #fff;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

/* ===== 顶部导航栏 ===== */
.top-header {
  height: 60px;
  background: #fff;
  border-bottom: 1px solid #e4e7ed;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 20px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.04);
  z-index: 100;
}

.top-header .header-left {
  display: flex;
  align-items: center;
  gap: 20px;
}

.logo-section {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 20px;
  font-weight: 600;
  color: #303133;
  cursor: pointer;
  transition: color 0.3s;
}

.logo-section i {
  font-size: 28px;
  color: #409EFF;
}

.logo-section:hover {
  color: #409EFF;
}

.user-menu {
  cursor: pointer;
}

.user-info-box {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 5px 10px;
  border-radius: 20px;
  transition: background 0.3s;
}

.user-info-box:hover {
  background: #f5f7f9;
}

.username-text {
  font-size: 14px;
  color: #606266;
  max-width: 100px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.user-card {
  padding: 5px 0;
  text-align: center;
}

.user-card-name {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 8px;
}

/* ===== 页面头部 ===== */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin: 20px 20px 30px 20px;
  background: #fff;
  padding: 30px;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.08);
}

.page-header .header-left {
  flex: 1;
}

.page-title {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 28px;
  font-weight: 600;
  color: #303133;
  margin: 0 0 8px 0;
}

.page-title i {
  font-size: 32px;
  color: #409EFF;
}

.page-subtitle {
  color: #909399;
  font-size: 14px;
  margin: 0;
}

.page-header .header-right {
  display: flex;
  align-items: center;
  gap: 15px;
  justify-content: flex-end
}

.search-section {
  display: flex;
  align-items: center;
  gap: 8px;
}

.search-box {
  width: 300px;
}

.search-box ::v-deep .el-input__inner {
  border-radius: 5px;
  background: #f5f7f9;
  border: 1px solid #e4e7ed;
}

.search-box ::v-deep .el-input__inner:focus {
  background: #fff;
  border-color: #409EFF;
}

.search-btn {
  flex-shrink: 0;
}

.action-buttons {
  display: flex;
  align-items: center;
  gap: 10px;
}


/* ===== 文集网格 ===== */
.collection-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 20px;
  margin: 0 20px 30px 20px;
}

.collection-card {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.08);
  padding: 24px;
  cursor: pointer;
  transition: all 0.3s;
  border: 1px solid transparent;
}

.collection-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 20px rgba(0,0,0,0.12);
  border-color: #409EFF;
}

/* ===== 卡片头部 ===== */
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
}

.collection-title {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
}

.title-icon {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
}

.title-icon.public-icon {
  background: #ecf5ff;
  color: #409EFF;
}

.title-icon.private-icon {
  background: #fdf6ec;
  color: #E6A23C;
}

.title-text {
  font-size: 18px;
  font-weight: 600;
  color: #303133;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
}

.collection-badge {
  flex-shrink: 0;
}

/* ===== 卡片元信息 ===== */
.card-meta {
  margin-bottom: 16px;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #909399;
  font-size: 13px;
  margin-bottom: 6px;
}

.meta-item i {
  font-size: 14px;
  width: 14px;
  text-align: center;
}

/* ===== 卡片描述 ===== */
.card-description {
  margin-bottom: 20px;
}

.description-text {
  color: #606266;
  font-size: 14px;
  line-height: 1.6;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* ===== 卡片底部 ===== */
.card-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 16px;
  border-top: 1px solid #f0f2f5;
}

.footer-left {
  color: #909399;
  font-size: 13px;
}

.doc-count {
  font-weight: 500;
}

.footer-right {
  opacity: 0;
  transition: opacity 0.3s;
}

.collection-card:hover .footer-right {
  opacity: 1;
}

/* ===== 空状态 ===== */
.empty-state {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.08);
  margin: 0 20px;
}

/* ===== 分页 ===== */
.pagination-wrapper {
  display: flex;
  justify-content: center;
  padding: 20px 0;
  /* background: #fff; */
  border-radius: 8px;
  /* box-shadow: 0 2px 12px rgba(0,0,0,0.08); */
  margin: 0 20px 20px 20px;
}

/* ===== 表单样式 ===== */
.form-tip {
  margin-left: 10px;
  font-size: 12px;
  color: #909399;
}

.icon-selector {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.icon-preview {
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f7f9;
  border-radius: 8px;
  border: 2px solid #e4e7ed;
  flex-shrink: 0;
  align-self: flex-start;
}

.icon-preview i {
  font-size: 32px;
  color: #409EFF;
}

.icon-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(50px, 1fr));
  gap: 10px;
  max-height: 300px;
  overflow-y: auto;
  padding: 10px;
  background: #fafafa;
  border-radius: 8px;
  border: 1px solid #e4e7ed;
}

.icon-grid.mobile {
  grid-template-columns: repeat(auto-fill, minmax(45px, 1fr));
  gap: 8px;
  max-height: 250px;
  padding: 8px;
}

.icon-item {
  width: 100%;
  aspect-ratio: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  border: 2px solid #e4e7ed;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
}

.icon-item:hover {
  border-color: #409EFF;
  background: #ecf5ff;
  transform: scale(1.05);
}

.icon-item.active {
  border-color: #409EFF;
  background: #ecf5ff;
  box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.2);
}

.icon-item i {
  font-size: 24px;
  color: #606266;
  transition: color 0.2s;
}

.icon-item:hover i,
.icon-item.active i {
  color: #409EFF;
}

.icon-grid.mobile .icon-item i {
  font-size: 20px;
}

/* 滚动条样式 */
.icon-grid::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

.icon-grid::-webkit-scrollbar-thumb {
  background: #d1dbe5;
  border-radius: 3px;
}

.icon-grid::-webkit-scrollbar-thumb:hover {
  background: #b4bccc;
}

.icon-grid::-webkit-scrollbar-track {
  background: transparent;
}

/* 对话框移动端样式 */
.collection-list.is-mobile {
  ::v-deep .collection-dialog {
    margin: 0 !important;
  }

  ::v-deep .collection-dialog .el-dialog {
    margin: 5vh auto !important;
    max-height: 90vh;
    display: flex;
    flex-direction: column;
  }

  ::v-deep .collection-dialog .el-dialog__header {
    padding: 15px 20px;
  }

  ::v-deep .collection-dialog .el-dialog__title {
    font-size: 18px;
  }

  ::v-deep .collection-dialog .el-dialog__body {
    padding: 15px 20px;
    max-height: calc(90vh - 120px);
    overflow-y: auto;
    flex: 1;
  }

  ::v-deep .collection-dialog .el-dialog__footer {
    padding: 10px 20px;
    border-top: 1px solid #e4e7ed;
  }

  ::v-deep .collection-dialog .el-dialog__footer .el-button {
    width: 100%;
    margin: 0;
  }

  ::v-deep .collection-dialog .el-dialog__footer .el-button + .el-button {
    margin-top: 10px;
  }

  ::v-deep .collection-dialog .el-form-item {
    margin-bottom: 18px;
  }

  ::v-deep .collection-dialog .el-form-item__label {
    font-size: 14px;
    padding-bottom: 8px;
  }

  ::v-deep .collection-dialog .el-radio-group {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  ::v-deep .collection-dialog .el-radio {
    margin-right: 0;
  }
}

/* ===== 响应式 ===== */
.collection-list.is-mobile .top-header {
  padding: 0 15px;
  height: 50px;
}

.collection-list.is-mobile .logo-section {
  font-size: 16px;
}

.collection-list.is-mobile .username-text {
  display: none;
}

.collection-list.is-mobile .collection-grid {
  grid-template-columns: 1fr;
  margin: 0 15px 20px 15px;
  gap: 15px;
}

.collection-list.is-mobile .page-header {
  flex-direction: column;
  gap: 15px;
  align-items: stretch;
  margin: 15px;
  padding: 20px;
}

.collection-list.is-mobile .page-title {
  font-size: 22px;
}

.collection-list.is-mobile .header-right {
  flex-direction: column;
  gap: 12px;
  align-items: stretch;
}

.collection-list.is-mobile .search-section {
  width: 100%;
}

.collection-list.is-mobile .search-box {
  width: 100%;
}

.collection-list.is-mobile .action-buttons {
  justify-content: flex-end;
  gap: 15px;
}

.collection-list.is-mobile .pagination-wrapper {
  margin: 0 15px 15px 15px;
}

.collection-list.is-mobile .pagination-wrapper .el-pagination {
  justify-content: center;
  flex-wrap: wrap;
}

.collection-list.is-mobile .footer-right {
  opacity: 1;
}

/* 小屏幕额外优化 */
@media (max-width: 480px) {
  .top-header {
    padding: 0 10px;
    height: 45px;
  }
  
  .logo-section {
    font-size: 14px;
  }
  
  .collection-list {
    padding: 0;
  }
  
  .page-header {
    margin: 10px;
    padding: 15px;
  }
  
  .page-title {
    font-size: 18px;
  }
  
  .page-subtitle {
    font-size: 12px;
  }
  
  .collection-grid {
    margin: 0 10px 15px 10px;
    gap: 12px;
  }
  
  .collection-card {
    padding: 12px;
  }
  
  .title-text {
    font-size: 16px;
  }
  
  .card-footer {
    flex-direction: column;
    gap: 10px;
    align-items: flex-start;
  }
  
  .footer-right {
    width: 100%;
  }
  
  .footer-right .el-button-group {
    width: 100%;
    display: flex;
  }
  
  .footer-right .el-button-group .el-button {
    flex: 1;
  }
  
  .pagination-wrapper {
    margin: 0 10px 10px 10px;
    padding: 10px 0;
  }
}
</style>
